// Client half of the billing address (Stripe Tax): Stripe's Address Element in billing mode, US
// only. POST /api/stripesubscription/billingAddress validates it, checks it with Stripe Tax and
// saves it (Subscriber.Billing*); the annual plan's booklet SHIPPING address stays separate.
// Plain CommonJS (like moduleAccess.js) so `node --test` can cover it.

// Options for elements.create('address', ...).
function addressElementOptions({ name, savedAddress } = {}) {
    const defaultValues = {};
    if (name && name.trim()) defaultValues.name = name.trim();
    defaultValues.address = savedAddress
        ? {
            line1: savedAddress.line1 || '',
            line2: savedAddress.line2 || '',
            city: savedAddress.city || '',
            state: savedAddress.state || '',
            postal_code: savedAddress.postal_code || '',
            country: 'US',
        }
        : { country: 'US' };
    return { mode: 'billing', allowedCountries: ['US'], defaultValues };
}

// Address Element value ({ name, address }) -> the API's billing address.
function toBillingPayload(value) {
    const a = value?.address;
    if (!a) return null;
    return {
        line1: a.line1 || '',
        line2: a.line2 || null,
        city: a.city || '',
        state: a.state || '',
        postal_code: a.postal_code || '',
        country: a.country || 'US',
    };
}

// The annual plan's shipping fields (StripeCard data names) filled from the billing address.
function shippingFromBilling(billing) {
    return {
        address: billing.line1,
        address2: billing.line2 || '',
        city: billing.city,
        state: billing.state,
        zipCode: billing.postal_code,
    };
}

// A failed save ({ status, data } from the API, or undefined on a network error) -> what to show.
// needsZipConfirmation: offer "use it anyway" and resend with confirmZipState: true.
function describeSaveError(response, address) {
    const data = response?.data;
    // Offer "use it anyway" only when the mismatch is the only problem; otherwise confirming would
    // just come back with the other error (and the user would never see it).
    const onlyZipProblem = Object.keys(data?.errors || {}).every((field) => field === 'postal_code');
    if (data?.zipStateMismatch && onlyZipProblem) {
        return {
            needsZipConfirmation: true,
            message: `ZIP code ${address?.postal_code ?? ''} doesn't look like it's in ${address?.state ?? 'that state'}. Please check it, or use it anyway if it's correct.`,
        };
    }
    // A specific field problem beats the generic message; the "address" key (Stripe Tax couldn't
    // place it) comes with an explanatory message, so show that instead.
    const fieldMessage = Object.entries(data?.errors || {}).find(([field, msg]) => field !== 'address' && msg)?.[1];
    return {
        needsZipConfirmation: false,
        message: fieldMessage || data?.message || "We couldn't save the billing address. Please try again.",
    };
}

function billingAddressSummary(address) {
    if (!address) return '';
    return [address.line1, address.line2, address.city, `${address.state} ${address.postal_code}`]
        .filter(Boolean)
        .join(', ');
}

module.exports = { addressElementOptions, toBillingPayload, shippingFromBilling, describeSaveError, billingAddressSummary };
